import { useMemo } from 'react';
import { NodeGraphPropsBase } from '@/core';
import { useDataQueryResult } from '../../../plugin-system';
import { DependencyMapConfig } from './dependency-map/dependency-map-config';
import { DependencyLink } from './dependency-map/types';
import { extractLinksFromQueryResults, NODE_GRAPH_DATA_TYPE } from './node-graph-model';

function formatQueryError(error: unknown): string | undefined {
  if (error === undefined || error === null) return undefined;
  if (error instanceof Error) return error.message;

  return String(error);
}

export interface UseNodeGraphPanelResult {
  links: DependencyLink[];
  /** Per-node self-traffic values (`from === to` in the source data). */
  selfLoops: ReadonlyMap<string, number>;
  /** Inline message for query failures or data-shape mismatches; `undefined` on success. */
  error: string | undefined;
  config: DependencyMapConfig;
}

export function useNodeGraphPanel({
  unit,
  thresholds,
  fromLabel,
  toLabel,
}: NodeGraphPropsBase): UseNodeGraphPanelResult {
  const { queryResults } = useDataQueryResult({ dataTypeFilter: NODE_GRAPH_DATA_TYPE });

  // Query failures (e.g. PromQL parse errors) surface inline rather than throwing.
  const queryError = queryResults.find((r) => r.error !== undefined && r.error !== null)?.error;
  const queryErrorMessage = formatQueryError(queryError);

  const {
    links,
    selfLoops,
    error: shapeError,
  } = useMemo(
    () => extractLinksFromQueryResults(queryResults, { fromLabel, toLabel }),
    [queryResults, fromLabel, toLabel]
  );

  // Query errors win over shape errors — a failed query has no useful shape to report on.
  const error = queryErrorMessage ?? shapeError;

  const config = useMemo<DependencyMapConfig>(() => ({ unit, thresholds }), [unit, thresholds]);

  return { links, selfLoops, error, config };
}
